'use client';

import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getPopupApi, putPopupMarkAsReadApi } from '@flipster/apis/v1/popup';
import { Popup, PopupId, PopupPage } from '@flipster/apis/v1/popup/types';
import { alert } from '@flipster/ui/Alert';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getErrorCode } from '@flipster/utils/error';

import { authBunja } from '../auth/state';
import { featureHighlightBunja } from './modal/feature-highlight/bunja';
import { followOnboardingBunja } from './modal/follow/bunja';
import { kycReminderBunja } from './modal/kyc/bunja';
import { leverageRestrictionPopupBunja } from './modal/leverage-limit-restrict/state';
import { marginBonusExpirationPopupBunja } from './modal/margin-bonus-expiration/state';

const getPopupApiQueryKey = 'getPopupApi';

export const entryPopupBlockBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const blockedPopupAtom = atom<PopupId[]>([]);
  function block(id: PopupId) {
    store.set(blockedPopupAtom, [...new Set([...store.get(blockedPopupAtom), id])]);
  }
  function unblock(id: PopupId) {
    store.set(
      blockedPopupAtom,
      store.get(blockedPopupAtom).filter((i) => i != id),
    );
  }
  function peek(id: PopupId) {
    return store.get(blockedPopupAtom).includes(id);
  }
  function peekAndUnblock(id: PopupId) {
    if (peek(id)) return unblock(id), true;
    return false;
  }
  return { block, unblock, peek, peekAndUnblock };
});
entryPopupBlockBunja.debugLabel = 'entryPopupBlockBunja';

export const entryPopupBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const entryPageAtom = atom<PopupPage | undefined>(undefined);
  const { peekAndUnblock } = bunja.use(entryPopupBlockBunja);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);
  const { showFollowOnboardingAtom, communityOnboardingBaseAtom } = bunja.use(followOnboardingBunja);
  const { isLoadingKycStatusAtom, isShowKycPopupAtom } = bunja.use(kycReminderBunja);
  const { showFeatureHighlightAtom, featureHighlightQueryAtom } = bunja.use(featureHighlightBunja);
  const { shouldShowMarginBonusExpirationPopupAtom, bonusExpirationStatusIsLoadingAtom } = bunja.use(
    marginBonusExpirationPopupBunja,
  );
  const { shouldShowLeverageLimitRestrictPopupAtom } = bunja.use(leverageRestrictionPopupBunja);

  const popupBaseAtom = atomWithQuery((get) => ({
    queryKey: [getPopupApiQueryKey],
    queryFn: () => getPopupApi(),
    enabled: !!get(isCookieLoggedInAtom),
    staleTime: Infinity,
    cacheTime: Infinity,
  }));

  const popupAtom = atom((get): Popup | undefined => {
    if (
      get(popupBaseAtom).isLoading ||
      get(communityOnboardingBaseAtom).isLoading ||
      get(isLoadingKycStatusAtom) ||
      get(featureHighlightQueryAtom).isLoading ||
      get(bonusExpirationStatusIsLoadingAtom)
    ) {
      return undefined;
    }

    const popups = [...(get(popupBaseAtom).data?.data.popups ?? [])];

    if (get(showFollowOnboardingAtom))
      popups.push({ id: 'ADHOC_FOLLOW_ONBOARDING', pages: ['WEB_MAIN', 'WEB_ASSETS'], description: '', data: {} });

    if (get(showFeatureHighlightAtom)) {
      popups.unshift({ id: 'ADHOC_FEATURE_HIGHLIGHT', description: '', data: {} });
    }
    if (get(shouldShowMarginBonusExpirationPopupAtom)) {
      popups.unshift({ id: 'ADHOC_MARGIN_BONUS_EXPIRATION', pages: ['WEB_TRADE'], description: '', data: {} });
    }
    if (get(shouldShowLeverageLimitRestrictPopupAtom)) {
      popups.unshift({ id: 'ADHOC_LEVERAGE_LIMIT_RESTRICT', pages: ['WEB_ASSETS'], description: '', data: {} });
    }
    if (get(isShowKycPopupAtom)) {
      popups.unshift({ id: 'ADHOC_KYC_REMINDER', pages: ['WEB_MAIN'], description: '', data: {} });
    }
    const entry = get(entryPageAtom);
    const top: Popup | undefined = popups.filter((popup) => {
      if (popup.pages === undefined) return true;
      return popup.pages.includes(entry ?? '');
    })[0];
    if (top && !peekAndUnblock(top.id)) return top;
  });
  popupAtom.debugLabel = 'popupAtom';

  function setEntryPage(page: PopupPage | undefined) {
    store.set(entryPageAtom, page);
  }

  return { showFollowOnboardingAtom, popupAtom, setEntryPage };
});
entryPopupBunja.debugLabel = 'entryPopupBunja';

export const PopupContext = createContext<Popup>(null!);
const PopupScope = createScopeFromContext(PopupContext);

export const entryPopupItemBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const popup = bunja.use(PopupScope);
  async function markAsRead(id: PopupId) {
    const queryClient = store.get(queryClientAtom);
    try {
      await putPopupMarkAsReadApi(id);
      // ignore alreadyMarkedAsRead
    } catch (error) {
      if (getErrorCode(error) !== 'api.popup.alreadyMarkedAsRead') alert.error(error);
    } finally {
      await queryClient.invalidateQueries({ queryKey: [getPopupApiQueryKey] });
    }
  }
  return { markAsRead, popup };
});
entryPopupItemBunja.debugLabel = 'entryPopupItemBunja';
